// src/pages/case-detail/components/ProductionLotsTab.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useProductionBatchStats } from '../../../hooks/useProductionBatchStats';

// Lot status colours (mirrors production_batches.status enum)
const LOT_STATUS_COLORS = {
  pending: 'bg-warning/15 text-warning border-warning/20',
  printing: 'bg-blue-500/15 text-blue-600 border-blue-500/20',
  post_processing: 'bg-purple-500/15 text-purple-600 border-purple-500/20',
  quality_check: 'bg-orange-500/15 text-orange-600 border-orange-500/20',
  completed: 'bg-success/15 text-success border-success/20',
  cancelled: 'bg-muted text-muted-foreground border-border',
  on_hold: 'bg-muted text-muted-foreground border-border',
};
const LOT_STATUS_LABELS = {
  pending: 'En attente',
  printing: 'Impression',
  post_processing: 'Post-traitement',
  quality_check: 'Contrôle qualité',
  completed: 'Terminé',
  cancelled: 'Annulé',
  on_hold: 'En pause',
};
const PRIORITY_COLORS = {
  low: 'text-muted-foreground',
  medium: 'text-blue-600',
  high: 'text-warning',
  urgent: 'text-error font-bold',
};

const BatchBadge = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
      LOT_STATUS_COLORS[status] ?? LOT_STATUS_COLORS.pending
    }`}
  >
    {LOT_STATUS_LABELS[status] ?? status}
  </span>
);

const BatchStatMini = ({ label, value, color = 'text-foreground' }) => (
  <div className="flex flex-col items-center bg-muted/60 rounded-lg px-4 py-2 min-w-[64px]">
    <span className={`text-lg font-bold ${color}`}>{value}</span>
    <span className="text-xs text-muted-foreground whitespace-nowrap">{label}</span>
  </div>
);

const BatchRow = ({ batch }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/40 transition-colors"
        onClick={() => setOpen((p) => !p)}
      >
        <div className="relative w-9 h-9 flex-shrink-0">
          <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
            <circle cx="18" cy="18" r="14" fill="none" stroke="var(--color-muted)" strokeWidth="3" />
            <circle
              cx="18"
              cy="18"
              r="14"
              fill="none"
              stroke={
                batch.progress >= 100
                  ? 'var(--color-success)'
                  : batch.progress >= 60
                    ? 'var(--color-primary)'
                    : 'var(--color-warning)'
              }
              strokeWidth="3"
              strokeDasharray={`${(batch.progress / 100) * 88} 88`}
              strokeLinecap="round"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-foreground">
            {batch.progress}%
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold">{batch.number}</p>
            <BatchBadge status={batch.status} />
            <span className={`text-xs ${PRIORITY_COLORS[batch.priority] ?? ''}`}>{batch.priority}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {batch.patientName} · {batch.clinicName}
          </p>
        </div>

        <div className="text-right flex-shrink-0 hidden sm:block">
          <p className="text-xs text-muted-foreground">Délai</p>
          <p className="text-xs font-medium">{batch.deadline}</p>
        </div>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} color="var(--color-muted-foreground)" />
      </div>

      {open && (
        <div className="border-t border-border bg-muted/20 px-4 pb-4 pt-3 space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            <div>
              <p className="text-xs text-muted-foreground">Début</p>
              <p className="font-medium">{batch.startDate}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Échéance</p>
              <p className="font-medium">{batch.deadline}</p>
            </div>
            {batch.completionDate && (
              <div>
                <p className="text-xs text-muted-foreground">Terminé le</p>
                <p className="font-medium text-success">{batch.completionDate}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground">Progression</p>
              <p className="font-medium">{batch.progress}%</p>
            </div>
          </div>
          {batch.notes && (
            <p className="text-xs text-muted-foreground italic border-t border-border pt-2">{batch.notes}</p>
          )}
        </div>
      )}
    </div>
  );
};

const ProductionBatchesCard = ({
  allBatches,
  caseBatches,
  allSummary,
  caseSummary,
  loading,
  error,
  caseId,
}) => {
  const batches = caseId ? caseBatches : allBatches;
  const summary = caseId ? caseSummary : allSummary;

  const completedCount = summary.completed ?? 0;
  const activeCount =
    (summary.printing ?? 0) + (summary.post_processing ?? 0) + (summary.quality_check ?? 0);
  const pendingCount = summary.pending ?? 0;

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Boxes" size={18} color="var(--color-primary)" />
        <h3 className="text-sm font-semibold text-foreground">Lots de production</h3>
      </div>

      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[1, 2].map((i) => (
            <div key={i} className="h-14 bg-muted rounded-xl" />
          ))}
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 p-3 bg-error/10 rounded-lg">
          <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
          <p className="text-xs text-error">{error}</p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            <BatchStatMini label="Total" value={batches.length} />
            <BatchStatMini label="En cours" value={activeCount} color="text-blue-600" />
            <BatchStatMini label="Terminés" value={completedCount} color="text-success" />
            <BatchStatMini label="En attente" value={pendingCount} color="text-warning" />
          </div>

          {Object.keys(summary).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 mb-4 pb-4 border-b border-border">
              {Object.entries(summary).map(([s, c]) => (
                <div key={s} className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground capitalize">
                    {LOT_STATUS_LABELS[s] ?? s}
                  </span>
                  <span className="text-xs font-semibold">{c}</span>
                </div>
              ))}
            </div>
          )}

          {batches.length === 0 ? (
            <div className="text-center py-8 space-y-2">
              <Icon name="PackageOpen" size={32} color="var(--color-muted-foreground)" className="mx-auto" />
              <p className="text-sm text-muted-foreground">
                {caseId
                  ? 'Aucun lot de production pour cette conception'
                  : 'Aucun lot de production trouvé'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {batches.map((b) => (
                <BatchRow key={b.id} batch={b} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * @param {string} clientUserId – `user_profiles.id` du praticien (cases.user_profile_id)
 * @param {string} caseId – `cases.id` de la fiche affichée
 */
const ProductionLotsTab = ({ clientUserId, caseId }) => {
  const {
    allBatches,
    caseBatches,
    allSummary,
    caseSummary,
    loading,
    error,
    refetch,
  } = useProductionBatchStats(clientUserId, caseId);

  if (!clientUserId) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground">
        Aucun praticien associé à cette conception — impossible de charger les lots.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-muted rounded-xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Lots de production</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Suivi des lots de fabrication liés à cette conception.
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-center border border-border rounded-xl bg-card">
          <Icon name="AlertTriangle" size={40} color="var(--color-error)" className="mb-3" />
          <p className="text-base font-semibold text-foreground mb-1">Erreur de chargement</p>
          <p className="text-sm text-muted-foreground mb-4 max-w-md">{error}</p>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={refetch}>
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Lots de production</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Suivi des lots de fabrication liés à cette conception.
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" onClick={refetch}>
          Actualiser
        </Button>
      </div>

      <ProductionBatchesCard
        allBatches={allBatches}
        caseBatches={caseBatches}
        allSummary={allSummary}
        caseSummary={caseSummary}
        loading={false}
        error={null}
        caseId={caseId}
      />
    </div>
  );
};

export default ProductionLotsTab;
